import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  reload,
  setPersistence,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../../firebase";
import { getSafeAccessError, platformAccessApi } from "../api/platformAccessApi";
import { useAccess } from "../auth/AccessProvider";
import AccessAlert from "../components/AccessAlert";
import AccessShell from "../components/AccessShell";
import RegistrationAccountForm from "../components/RegistrationAccountForm";
import RegistrationStepper from "../components/RegistrationStepper";
import VerificationPanel from "../components/VerificationPanel";
import {
  getTenantInvitationStage,
  isInvitationEmailMismatchError,
} from "../utils/tenantInvitation";
import { sendBrandedVerificationEmail } from "../utils/sendVerificationEmail";

const VERIFICATION_POLL_MS = 5000;

function getActionCodeSettings(token) {
  return { url: `${window.location.origin}/tenant-admin/register?invite=${encodeURIComponent(token)}`, handleCodeInApp: false };
}

function invitationShape(data) {
  const invitation = data?.invitation || data || {};
  return {
    ...invitation,
    companyDisplayName: invitation.companyDisplayName || invitation.displayName || invitation.companyName,
    maskedEmail: invitation.maskedEmail || invitation.emailMasked,
  };
}

function invitationEmailMismatchMessage(invitation) {
  const recipient = invitation?.maskedEmail || "the invited email address";
  return `This invitation was sent to ${recipient}. Switch accounts to continue securely.`;
}

export default function TenantAdminRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => String(searchParams.get("invite") || "").trim(), [searchParams]);
  const {
    loading: accessLoading,
    refreshAccessContext,
    signOutUser,
    user,
  } = useAccess();
  const [stage, setStage] = useState("validating_invitation");
  const [invitation, setInvitation] = useState(null);
  const [displayName, setDisplayName] = useState(auth.currentUser?.displayName || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [existingEmail, setExistingEmail] = useState("");
  const [showExistingLogin, setShowExistingLogin] = useState(false);
  const switchedFromEmail = useRef("");
  const verificationCheckInFlightForUser = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (accessLoading) {
      return () => {
        active = false;
      };
    }
    if (!token) {
      setStage("invalid_or_expired_invitation");
      setError("This registration link is incomplete. Ask your Platform Administrator for a new invitation.");
      return () => {
        active = false;
      };
    }

    setStage("validating_invitation");
    setError("");
    const currentUser = user || auth.currentUser;
    platformAccessApi
      .validateTenantAdminInvitation(token, currentUser?.email)
      .then(async (result) => {
        if (!active) return;
        const nextInvitation = invitationShape(result);
        setInvitation(nextInvitation);
        if (currentUser) {
          setDisplayName(currentUser.displayName || "");
          const nextStage = getTenantInvitationStage(
            currentUser,
            nextInvitation,
          );
          if (nextStage === "account_mismatch") {
            const previousEmail = currentUser.email || "the previous account";
            switchedFromEmail.current = previousEmail;
            setError("");
            setMessage(`Switching from ${previousEmail} so the invited account can continue.`);
            try {
              await signOutUser();
              if (!active) return;
              setDisplayName("");
              setExistingEmail("");
              setShowExistingLogin(false);
              setStage("account_form");
              setError("");
              setMessage(`We signed out ${previousEmail}. Create or sign in to the account that received this invitation.`);
            } catch (signOutError) {
              if (!active) return;
              switchedFromEmail.current = "";
              setMessage("");
              setStage("account_mismatch");
              setError(getSafeAccessError(signOutError, "We could not switch accounts automatically. Choose an option below to continue."));
            }
          } else {
            setStage(nextStage);
          }
        } else {
          setStage("account_form");
          if (switchedFromEmail.current) {
            setMessage(`We signed out ${switchedFromEmail.current}. Create or sign in to the account that received this invitation.`);
            switchedFromEmail.current = "";
          }
        }
      })
      .catch((nextError) => {
        if (!active) return;
        setStage("invalid_or_expired_invitation");
        setError(getSafeAccessError(nextError, "This invitation is invalid, expired, or has already been used."));
      });
    return () => {
      active = false;
    };
  }, [accessLoading, signOutUser, token, user]);

  const createAccount = async ({ firstName, surname, email, password }) => {
    setBusy(true);
    setError("");
    try {
      const validation = invitationShape(
        await platformAccessApi.validateTenantAdminInvitation(token, email),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true) {
        setError(invitationEmailMismatchMessage(validation));
        return;
      }
      const name = `${firstName} ${surname}`.trim();
      await setPersistence(auth, browserSessionPersistence);
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName: name });
      await sendBrandedVerificationEmail(credential.user, {
        continuePath: `/tenant-admin/register?invite=${encodeURIComponent(token)}`,
        fallbackSettings: getActionCodeSettings(token),
      });
      setDisplayName(name);
      setStage("email_verification_required");
      setMessage("Your account was created. Verify your email to accept the invitation.");
    } catch (nextError) {
      const safeMessage = getSafeAccessError(nextError, "We could not create your account. Please try again.");
      setError(safeMessage);
      if (nextError?.code === "auth/email-already-in-use") {
        setExistingEmail(email);
        setShowExistingLogin(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const signInExistingAccount = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await setPersistence(auth, browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(
        auth,
        String(data.get("email") || "").trim(),
        String(data.get("password") || ""),
      );
      await credential.user.getIdToken(true);
      const validation = invitationShape(
        await platformAccessApi.validateTenantAdminInvitation(
          token,
          credential.user.email,
        ),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true) {
        setShowExistingLogin(false);
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(validation));
        return;
      }
      setDisplayName(credential.user.displayName || "");
      setStage(credential.user.emailVerified ? "accepting_invitation" : "email_verification_required");
      setShowExistingLogin(false);
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not sign in to the existing account."));
    } finally {
      setBusy(false);
    }
  };

  const checkVerification = useCallback(async ({ automatic = false } = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      if (!automatic) navigate("/login", { replace: true });
      return false;
    }
    const uid = currentUser.uid;
    if (verificationCheckInFlightForUser.current === uid) return false;
    verificationCheckInFlightForUser.current = uid;
    if (!automatic) {
      setBusy(true);
      setError("");
    }
    try {
      await reload(currentUser);
      if (!mounted.current || auth.currentUser?.uid !== uid) return false;
      if (!currentUser.emailVerified) {
        if (!automatic) {
          setError("Your email is not verified yet. Complete verification, then try again.");
        }
        return false;
      }
      await currentUser.getIdToken(true);
      const validation = invitationShape(
        await platformAccessApi.validateTenantAdminInvitation(
          token,
          currentUser.email,
        ),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true) {
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(validation));
        return false;
      }
      setError("");
      setMessage("Email verified. Your invitation is ready to accept.");
      setStage("accepting_invitation");
      return true;
    } catch (nextError) {
      if (!automatic && mounted.current) {
        setError(getSafeAccessError(nextError, "We could not confirm email verification."));
      }
      return false;
    } finally {
      if (verificationCheckInFlightForUser.current === uid) {
        verificationCheckInFlightForUser.current = null;
      }
      if (!automatic && mounted.current) setBusy(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    if (stage !== "email_verification_required" || busy) return undefined;
    const runCheck = () => {
      if (document.visibilityState === "visible") {
        void checkVerification({ automatic: true });
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    runCheck();
    const interval = window.setInterval(runCheck, VERIFICATION_POLL_MS);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [busy, checkVerification, stage]);

  const switchToInvitedAccount = async (showLogin) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await signOutUser();
      setDisplayName("");
      setExistingEmail("");
      setShowExistingLogin(showLogin);
      setStage("account_form");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not switch accounts. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    try {
      await sendBrandedVerificationEmail(auth.currentUser, {
        continuePath: `/tenant-admin/register?invite=${encodeURIComponent(token)}`,
        fallbackSettings: getActionCodeSettings(token),
      });
      setMessage("A new verification email has been sent.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not resend verification. Please wait and try again."));
    } finally {
      setBusy(false);
    }
  };

  const completeInvitation = async () => {
    setBusy(true);
    setError("");
    try {
      const currentUser = auth.currentUser;
      if (!currentUser?.emailVerified) throw new Error("Verify your email before accepting the invitation.");
      await currentUser.getIdToken(true);
      await platformAccessApi.completeTenantAdminRegistration(
        token,
        displayName || currentUser.displayName || currentUser.email,
      );
      const context = await refreshAccessContext({ forceTokenRefresh: true });
      if (!context?.activeTenant) throw new Error("Tenant access could not be confirmed.");
      window.history.replaceState({}, document.title, "/tenant-admin/dashboard");
      navigate("/tenant-admin/dashboard", { replace: true });
    } catch (nextError) {
      if (isInvitationEmailMismatchError(nextError)) {
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(invitation));
      } else {
        setError(getSafeAccessError(nextError, "We could not accept this invitation. Confirm that you signed in with the invited email."));
      }
    } finally {
      setBusy(false);
    }
  };

  const activeStep = ["account_form", "account_mismatch"].includes(stage) ? 0 :
    stage === "email_verification_required" ? 1 : 2;

  return (
    <AccessShell
      eyebrow="Tenant administrator"
      title={stage === "invalid_or_expired_invitation" ? "Invitation unavailable." : "Create your account."}
      description={invitation?.companyDisplayName ? `Join ${invitation.companyDisplayName} on AndUs.` : "Use your secure tenant invitation to get started."}
      footer={<p>Already have an account? <button className="pa-inline-link" type="button" onClick={() => setShowExistingLogin(true)}>Sign in</button></p>}
    >
      {stage !== "invalid_or_expired_invitation" && stage !== "validating_invitation" ? <RegistrationStepper activeStep={activeStep} /> : null}
      <AccessAlert tone="error">{error}</AccessAlert>
      <AccessAlert tone="success">{message}</AccessAlert>

      {stage === "validating_invitation" ? <div className="pa-inline-loader"><span />Validating your invitation…</div> : null}
      {stage === "invalid_or_expired_invitation" ? (
        <div className="pa-empty-action"><p>Tenant registration is invitation-only and does not use an access code.</p><Link className="pa-primary-button" to="/login"><span>Return to sign in</span><span>›</span></Link></div>
      ) : null}
      {stage === "account_mismatch" && !showExistingLogin ? (
        <div className="pa-accept-panel">
          <div className="pa-invitation-workspace">
            <span>Different account required</span>
            <strong>{invitation?.companyDisplayName}</strong>
            <small>Invitation for {invitation?.maskedEmail}</small>
          </div>
          <p>
            You are signed in as <strong>{auth.currentUser?.email}</strong>. For security,
            only the email address that received this invitation can accept it.
          </p>
          <button
            className="pa-primary-button"
            type="button"
            onClick={() => switchToInvitedAccount(false)}
            disabled={busy}
          >
            <span>{busy ? "Switching accounts..." : "Create the invited account"}</span>
            <span aria-hidden="true">&gt;</span>
          </button>
          <button
            className="pa-text-button"
            type="button"
            onClick={() => switchToInvitedAccount(true)}
            disabled={busy}
          >
            Sign in with the invited account
          </button>
        </div>
      ) : null}
      {stage === "account_form" && !showExistingLogin ? (
        <RegistrationAccountForm onSubmit={createAccount} submitting={busy} invitation={invitation} />
      ) : null}
      {showExistingLogin ? (
        <form className="pa-form pa-existing-login" onSubmit={signInExistingAccount}>
          <div className="pa-invitation-workspace"><span>Existing account</span><strong>Sign in to accept this invitation</strong></div>
          <label className="pa-field"><span>Work email</span><input name="email" type="email" defaultValue={existingEmail} required /></label>
          <label className="pa-field"><span>Password</span><input name="password" type="password" autoComplete="current-password" required /></label>
          <button className="pa-primary-button" type="submit" disabled={busy}><span>{busy ? "Signing in…" : "Sign in and continue"}</span><span>›</span></button>
          <button className="pa-text-button" type="button" onClick={() => setShowExistingLogin(false)}>Create a new account instead</button>
        </form>
      ) : null}
      {stage === "email_verification_required" && !showExistingLogin ? (
        <VerificationPanel email={auth.currentUser?.email} onContinue={() => checkVerification()} onResend={resendVerification} busy={busy} />
      ) : null}
      {stage === "accepting_invitation" && !showExistingLogin ? (
        <div className="pa-accept-panel">
          <div className="pa-invitation-workspace"><span>Verified invitation</span><strong>{invitation?.companyDisplayName}</strong><small>{auth.currentUser?.email}</small></div>
          <p>Accepting creates your Tenant Administrator membership for this company. It does not grant Platform access.</p>
          <button className="pa-primary-button" type="button" onClick={completeInvitation} disabled={busy}><span>{busy ? "Activating access…" : "Accept invitation"}</span><span>›</span></button>
        </div>
      ) : null}
    </AccessShell>
  );
}
